import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, from, map, of, switchMap, throwError } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { BrowserService } from './browser.service';
import { DataLoaderService } from './data-loader.service';
import { GuestService } from './guest.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class MergeAccountService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private state = inject(StateService);
  private storage = inject(StorageService);
  private guestService = inject(GuestService);
  private browserService = inject(BrowserService);
  private loader = inject(DataLoaderService);
  merging = false;
  private _merged: Subject<boolean> = new BehaviorSubject<boolean>(undefined);
  public readonly onMerged: Observable<boolean> = this._merged.asObservable();

  public mergeGuestByAnonymousId(anonymous_id: string): Observable<boolean> {
    return this.http.post(`${this.config.apiEndpoint}/accounts/merge`, { anonymous_id }).pipe(
      map(() => {
        this._merged.next(true);
        this.loader.remove('guest');
        return true;
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          return throwError(() => err);
        } else {
          console.error('Error merging guest by anonymous ID:', err);
          return of(false);
        }
      })
    );
  }

  // Merging a guest account into a logged in user account if the email matches or the anonymous ID exists
  public autoMerge(): Observable<boolean> {
    if (this.merging) return of(false);
    const guestAuthToken = this.storage.getAny('guest-auth');
    if (!guestAuthToken) return of(false);
    this.merging = true;
    this.state.guestAuthToken = guestAuthToken;
    return from(this.guestService.getGuest()).pipe(
      switchMap(guest => {
        this.storage.remove('guest-auth');
        this.storage.removeCookie(
          'guest-auth',
          this.browserService.getTenantCustomDomain(),
          this.browserService.getCorrectPicflowUrl()
        );
        delete this.state.guestAuthToken;
        if (!guest) return of(false);

        // Merging the guest account with the logged in user
        this.merging = false;
        if (guest.anonymous_id) {
          return this.mergeGuestByAnonymousId(guest.anonymous_id);
        }

        return of(false);
      })
    );
  }
}
